import { authLog } from 'src/sentry';
import type { AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { LinearProgress } from '@mui/material';

import useAxios from 'src/hooks/use-axios';
import { useAuth } from 'src/hooks/use-auth';
import { useUser } from 'src/hooks/use-user';

import { reportDeviceTimezone } from 'src/utils/report-device-timezone';

import type { Organization, PatientProfile } from 'src/types/api';

type Props = {
  element: React.ReactElement<{ id?: string }> | null;
};

function RequireAuth({ element }: Props) {
  const id = element?.props.id;
  const { user, setUser } = useUser();
  const location = useLocation();
  const { auth } = useAuth();
  const api = useAxios();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoading(true);
      let profile: PatientProfile | undefined;
      try {
        profile = (await api.get<PatientProfile>('/v1/users/profile/')).data;

        // PROD-1806: every launch and login passes through this profile fetch.
        reportDeviceTimezone(api, profile.id);

        if (profile.organization_id) {
          const organization = (await api.get<Organization>(`/v1/organizations/${profile.organization_id}/`)).data;
          profile = { ...profile, organization };
        }
      } catch (err) {
        authLog('profile fetch failed', {
          status: (err as AxiosError)?.response?.status,
          url: (err as AxiosError)?.config?.url,
          hasAuthAfter: !!auth,
        });
      } finally {
        setUser(profile);
        setIsLoading(false);
      }
    };

    if (auth) fetchUserProfile();
  }, [api, auth, setUser]);

  const hasAccess = user?.access.includes(id as string);

  const redirectPath =
    location.pathname !== '/' ? `?next=${encodeURIComponent(location.pathname + location.search)}` : '';

  if (!auth) return <Navigate to={`/login/${redirectPath}`} state={{ from: location }} replace />;

  if (isLoading) return <LinearProgress sx={{ mx: { xs: '2vh', lg: 25 }, mt: { xs: '40vh', lg: 'auto' } }} />;

  // profile fetch failed (no user) — treat as an auth problem and send back to login
  if (!user) return <Navigate to={`/login/${redirectPath}`} state={{ from: location }} replace />;

  if (!element) return <Navigate to="/app" state={{ from: location }} replace />;

  if (!hasAccess) return <Navigate to="/404" state={{ from: location }} replace />;

  return element;
}

export default RequireAuth;
